// Suggestion engine — deterministic, scaffold-grounded improvement opportunities.
//
// Mirrors the drift engine (src/drift/index.ts): build a shared context once,
// run every provider over it, collect and threshold-filter the results. Detection
// uses no language model; applying a suggestion is delegated elsewhere.

import type {
  CaiConfig,
  ParsedFile,
  Suggestion,
  SuggestContext,
  SuggestSettings,
  SuggestionProvider,
} from "../types.js";
import { findScaffoldFiles } from "../drift/index.js";
import { parseScaffoldFile } from "../drift/frontmatter.js";
import { churnByFile } from "../git.js";
import { defaultProviders } from "./registry.js";

const DEFAULT_SETTINGS: SuggestSettings = {
  overlapThreshold: 0.5,
  hotspotMinCommits: 5,
  savingFloor: 0,
};

export interface SuggestReport {
  suggestions: Suggestion[];
  filesScanned: number;
}

/** Run all suggestion providers and return the thresholded suggestions. */
export async function runSuggest(
  config: CaiConfig,
  providers: SuggestionProvider[] = defaultProviders
): Promise<SuggestReport> {
  const { projectRoot, scaffoldRoot } = config;
  const settings = resolveSettings(config.settings.suggest);

  const files: ParsedFile[] = [];
  for (const filePath of findScaffoldFiles(projectRoot, scaffoldRoot)) {
    const parsed = parseScaffoldFile(filePath, projectRoot);
    if (parsed) files.push(parsed);
  }

  const churn = await churnByFile(projectRoot);

  const ctx: SuggestContext = {
    projectRoot,
    scaffoldRoot,
    files,
    churn,
    settings,
  };

  const all: Suggestion[] = [];
  for (const provider of providers) {
    all.push(...(await provider.run(ctx)));
  }

  const suggestions = all
    .filter((s) => s.estimatedSaving >= settings.savingFloor)
    .sort((a, b) => b.estimatedSaving - a.estimatedSaving);

  return { suggestions, filesScanned: files.length };
}

function resolveSettings(partial: Partial<SuggestSettings> | undefined): SuggestSettings {
  return {
    overlapThreshold: partial?.overlapThreshold ?? DEFAULT_SETTINGS.overlapThreshold,
    hotspotMinCommits: partial?.hotspotMinCommits ?? DEFAULT_SETTINGS.hotspotMinCommits,
    savingFloor: partial?.savingFloor ?? DEFAULT_SETTINGS.savingFloor,
  };
}
